import { useEffect } from "react";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { GENERAL_ERROR } from "@/react/components/Paywalls/Checkout/helpers";
import {
  processWithRetries,
  redirectToCheckoutConfirmation,
} from "@/react/components/Paywalls/Checkout/hooks/paymentMethods/helpers";
import { ApiError } from "@/react/config/CustomErrors";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import {
  isCheckoutAttemptCompleted,
  isCheckoutAttemptDelayedConfirmation,
  isCheckoutAttemptProcessingOrWaitingForConfirmation,
} from "@/react/helpers/checkoutAttemptHelpers";
import { isBancontact, isIdeal } from "@/react/helpers/paywallCheckoutHelpers";
import type { CheckoutAttemptStatus } from "@/react/types/PaywallCheckoutAttempt";
import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import { Loader } from "@circle-react-uikit/Loader";
import { useVerificationQueryParams } from "./hooks/useVerificationQueryParams";

/*
  Architectural Decision: URL-Based State Management for Resilience

  This component acts as the verifier for any asynchronous checkout attempt, such as
  redirect-based payments (Klarna, Affirm) or card payments that require 3DS authentication.

  It is intentionally designed to read its initial state (`payment_intent_id` and `payment_method_type`)
  directly from the URL query parameters. This is a critical design choice for resilience.
  A payment flow can be easily interrupted (e.g., user switches apps, browser tab is suspended, page is refreshed).
  By using the URL as the single source of truth, we create a durable state that can survive these interruptions.
  If the page reloads, this component will re-read the URL and seamlessly continue the verification process.

  An alternative using in-memory React Context state was considered but rejected due to its fragility.
  Context state is lost on page reload, which would break the checkout flow and leave the user in a stuck state.
  This URL-based approach aligns with the robust, existing pattern for all other redirect-based payments in the system.
*/

interface CheckoutResponseFailure {
  failure: {
    message?: string;
  };
}

interface CheckoutResponseSuccess {
  checkout_attempt_status: CheckoutAttemptStatus;
}

type CheckoutResponse =
  | null
  | CheckoutResponseFailure
  | CheckoutResponseSuccess;

export const CheckoutVerificationHandler = () => {
  const {
    checkoutConfirmationUrl,
    handleError,
    isPerformingVerification,
    setIsPerformingVerification,
  } = usePaywallCheckoutContext();

  const { invoiceId, paymentIntent, paymentMethodType, wasForwarded } =
    useVerificationQueryParams();

  const resetURL = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("invoice_id");
    url.searchParams.delete("payment_intent");
    url.searchParams.delete("payment_intent_client_secret");
    url.searchParams.delete("redirect_status");
    url.searchParams.delete("payment_method_type");
    url.searchParams.delete("forwarded");
    window.history.replaceState({}, "", url);
  };

  const verifyMutation = useMutation(() =>
    paywallCheckoutApi.verifications.create({
      invoice_id: invoiceId,
      payment_intent_id: paymentIntent,
      payment_method_type: paymentMethodType,
    }),
  );

  const verifyCheckoutWasProcessed = async (): Promise<any> => {
    try {
      const verifyResponse: CheckoutResponse =
        await verifyMutation.mutateAsync();

      if (!verifyResponse || typeof verifyResponse !== "object") {
        return { failure: { message: GENERAL_ERROR } };
      }

      if ("failure" in verifyResponse) {
        return verifyResponse;
      }

      if ("checkout_attempt_status" in verifyResponse) {
        if (
          isCheckoutAttemptProcessingOrWaitingForConfirmation(
            verifyResponse.checkout_attempt_status,
          )
        ) {
          return null;
        }
        return verifyResponse;
      }
    } catch (e: any) {
      if (e instanceof ApiError) {
        return e.body;
      }
      notifyBugsnag(e);
      return { failure: { message: e.message || GENERAL_ERROR } };
    }
  };

  const handleTerminalFailure = (error_message: string | null) => {
    const message = error_message || GENERAL_ERROR;

    // Bancontact/iDEAL workaround: Force a reload on failed payments to prevent showing stale data.
    // When payment fails after redirect from Bancontact/iDEAL, the checkout resources (member,
    // subscription, charge) remain until Stripe's webhook triggers cleanup. Since the checkout
    // page doesn't dynamically reload member data, it incorrectly shows "already purchased".
    // This redirect ensures the page reloads after cleanup, matching the behavior of other
    // payment methods. The "forwarded" flag prevents infinite loops. This tech debt will be
    // addressed in Q4 as part of the checkout system refactor.
    if (
      !wasForwarded &&
      (isBancontact(paymentMethodType) || isIdeal(paymentMethodType))
    ) {
      const url = new URL(window.location.href);
      url.searchParams.append("forwarded", "true");
      window.location.replace(url.toString());
      return;
    }

    // We delay this to allow the initial Checkout#preview response to load.
    // Otherwise the error message will be cleared and the user won't see it
    setTimeout(() => {
      handleError({
        message,
        disablePayButton: false,
      });
      setIsPerformingVerification(false);
      resetURL();
    }, 2000);
  };
  const performVerification = async () => {
    if (!invoiceId && !paymentIntent) return;

    setIsPerformingVerification(true);

    try {
      const checkoutResponse = await processWithRetries({
        executor: verifyCheckoutWasProcessed,
      });

      handleCheckoutResponse(checkoutResponse);
    } catch {
      handleTerminalFailure(null);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: CheckoutResponse) => {
    if (checkoutResponse === null) {
      handleVerificationFailed();
      return;
    }

    if ("failure" in checkoutResponse) {
      const errorMessage = checkoutResponse?.failure?.message || null;
      handleTerminalFailure(errorMessage);
      return;
    }

    if (isCompletedCheckout(checkoutResponse)) {
      redirectToCheckoutConfirmation(checkoutResponse, checkoutConfirmationUrl);
      return;
    }

    // Handle unexpected response format
    handleTerminalFailure(null);
  };

  const isCompletedCheckout = (
    response: CheckoutResponseSuccess | CheckoutResponseFailure,
  ) =>
    "checkout_attempt_status" in response &&
    (isCheckoutAttemptCompleted(response.checkout_attempt_status) ||
      isCheckoutAttemptDelayedConfirmation(response.checkout_attempt_status));

  const handleVerificationFailed = () => {
    handleError({
      message: GENERAL_ERROR,
      disablePayButton: false,
    });
    setIsPerformingVerification(false);
    resetURL();
  };

  useEffect(() => {
    void performVerification();
  }, [invoiceId, paymentIntent]);

  if (isPerformingVerification) {
    return (
      <Loader
        variant="fullscreen"
        message={t("paywalls.checkout.bnpl_processing")}
      />
    );
  }

  return null;
};
